<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import { storeToRefs } from "pinia";
import { RotateCcw, Trash2, Plus } from "lucide-vue-next";
import AiStar from "@/components/episto-ui/ai/AiStar.vue";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
import BulkAddMessageBox from "@/components/Survey/Edit/BulkAddMessageBox.vue";
import useMyFetch from "@stores/useMyFetch";
import { useTaskProgressesStore } from "@stores/taskProgresses";
import { getClientId } from "@utils/clientId";
import { captureEvent } from "@/lib/analytics";

const props = defineProps({
  questionId: { type: Number, required: true },
  choices: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["applied", "cancel"]);

const themes = ref([]);
const otherThemeTitle = ref(null);
const loading = ref(false);
const creating = ref(false);
const showApplyDialog = ref(false);
const showResetDialog = ref(false);

const { lastProgressUpdate } = storeToRefs(useTaskProgressesStore());

const hasThemes = computed(() => {
  const entries = Object.values(props.choices || {});
  return entries.filter((c) => !["997", "999"].includes(c.code)).length > 0;
});

function initThemes() {
  const entries = Object.values(props.choices || {});
  const filtered = entries.filter((c) => !["997", "999"].includes(c.code));
  themes.value = filtered.length >= 2 ? filtered.map((c) => c.title) : ["Theme 1", "Theme 2"];
  otherThemeTitle.value = entries.find((c) => c.code === "997")?.title || null;
}

onMounted(async () => {
  initThemes();
  const { data } = await useMyFetch(`/questions/${props.questionId}/tags_status`).get().json();
  creating.value = data.value?.status === "creating";
});

// Watch for task progress updates related to this question's tag generation
watchEffect(() => {
  if (
    ["Complete", "Failed"].includes(lastProgressUpdate.value.status) &&
    lastProgressUpdate.value.taskInfo?.type === "create_themes" &&
    lastProgressUpdate.value.taskInfo?.questionId === props.questionId
  ) {
    creating.value = false;
    if (lastProgressUpdate.value.status === "Complete") {
      emit("applied");
    }
  }
});

function addTheme() {
  themes.value.push("");
}

function removeTheme(index) {
  themes.value.splice(index, 1);
}

function bulkAddThemes(list) {
  if (list) themes.value = list;
}

async function generateWithAI() {
  loading.value = true;
  const { response } = await useMyFetch(`/questions/${props.questionId}/new_tags`).post({
    tags: null,
    client_id: getClientId(),
  });

  if (response.value.ok) captureEvent("verbatim_themes_generated", { question_id: props.questionId });
  loading.value = false;
  creating.value = true;
}

async function applyThemes() {
  const tags = themes.value.map((t) => t.trim()).filter((t) => t !== "");
  if (!tags.length) return;
  loading.value = true;
  const { response } = await useMyFetch(`/questions/${props.questionId}/new_tags`).post({
    tags,
    client_id: getClientId(),
  });
  loading.value = false;
  showApplyDialog.value = false;
  creating.value = true;

  if (response.value.ok) {
    captureEvent("verbatim_themes_applied", { question_id: props.questionId, themes_count: tags.length });
  }
}

async function resetThemes() {
  loading.value = true;
  const { response } = await useMyFetch(`/questions/${props.questionId}/new_tags`).post({
    tags: null,
    client_id: getClientId(),
  });
  loading.value = false;
  showResetDialog.value = false;
  creating.value = true;

  if (response.value.ok) captureEvent("verbatim_themes_reset", { question_id: props.questionId });
}
</script>

<template>
  <!-- In progress state -->
  <div v-if="creating" class="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
    <Spinner class="h-5 w-5" />
    <p class="text-13">Codification is in progress...</p>
  </div>

  <!-- No themes yet: propose AI generation -->
  <div v-else-if="!hasThemes" class="flex flex-col items-center justify-center gap-4 py-12">
    <p class="text-13 text-muted-foreground">No themes yet</p>
    <div class="flex gap-2">
      <Button
        :disabled="loading"
        @click="generateWithAI">
        <AiStar class="h-4 w-4 mr-2" />
        Generate with AI
      </Button>
      <Button
        variant="outline"
        @click="initThemes()">
        Create manually
      </Button>
    </div>
  </div>

  <!-- Theme editor -->
  <div v-else class="flex flex-col gap-4 p-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 class="heading-section">Edit themes</h3>
      <Button
        variant="ghost"
        size="sm"
        class="text-muted-foreground"
        @click="showResetDialog = true">
        <RotateCcw class="h-3.5 w-3.5 mr-1.5" />
        Reset with AI
      </Button>
    </div>

    <!-- Themes list -->
    <div class="flex flex-col gap-2">
      <div
        v-for="(_theme, index) in themes"
        :key="index"
        class="flex items-center gap-2">
        <Badge
          variant="outline"
          class="h-6 w-6 justify-center text-xs font-normal shrink-0">
          {{ index + 1 }}
        </Badge>
        <Input
          v-model="themes[index]"
          placeholder="Theme name"
          class="h-8 flex-1" />
        <Button
          variant="ghost"
          size="icon"
          class="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
          @click="removeTheme(index)">
          <Trash2 class="h-3.5 w-3.5" />
        </Button>
      </div>

      <!-- Other row (read-only) -->
      <div
        v-if="otherThemeTitle"
        class="flex items-center gap-2">
        <Badge
          variant="outline"
          class="h-6 w-6 justify-center text-xs font-normal shrink-0">
          {{ themes.length + 1 }}
        </Badge>
        <Input
          :model-value="otherThemeTitle"
          disabled
          class="h-8 flex-1" />
        <div class="h-7 w-7 shrink-0" />
      </div>
    </div>

    <!-- Add / Bulk add -->
    <div class="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        @click="addTheme">
        <Plus class="h-3.5 w-3.5 mr-1" />
        Add theme
      </Button>
      <BulkAddMessageBox
        field-name="themes"
        :field-titles="themes"
        unrestricted
        size="sm"
        @bulk-add="bulkAddThemes" />
    </div>

    <!-- Footer -->
    <div class="flex items-center justify-end gap-2 pt-4 border-t">
      <Button
        variant="outline"
        @click="$emit('cancel')">
        Cancel
      </Button>
      <Button
        :disabled="loading"
        @click="showApplyDialog = true">
        <Spinner v-if="loading" class="mr-2" />
        Apply
      </Button>
    </div>
  </div>

  <ConfirmDialog
    v-model:open="showApplyDialog"
    title="Apply new themes?"
    description="Your previous themes will be lost. Are you sure you want to apply your themes?"
    confirm-label="Apply"
    variant="default"
    :loading="loading"
    :auto-close="false"
    @confirm="applyThemes" />

  <ConfirmDialog
    v-model:open="showResetDialog"
    title="Reset themes?"
    description="Your previous themes will be lost. The AI will find the best themes from scratch."
    confirm-label="Reset"
    variant="default"
    :loading="loading"
    :auto-close="false"
    @confirm="resetThemes" />
</template>
